import {
  AnalyticsTrackingEvent,
  CloseButton,
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
  brandclubStyles,
  getEntityImage,
  getRetailerFromId,
  hexadecimal,
} from "@brandclub/common-ui";
import { Box, Drawer, Typography, styled } from "@mui/material";
import { FC } from "react";

import React from "react";

import pluralize from "pluralize";

import { useTrackActions } from "../../hooks/useTracking";
import {
  calculateTotal,
  calculateTotalOfAllCarts,
  sumItemQuantities,
} from "../../utils";
import { HOST_DOMAIN } from "../../utils/domain";
import { CartProduct, CartProductLoading } from "./CartProduct";
import { EmptyBagMessageCard } from "./EmptyBagMessageCard";
import { useBCRouting } from "../../providers/BCRoutingContextProvider";
import { create } from "zustand";
import { useAllCarts } from "@/api/cache/cartQueryOptions";
import { UserRetailerShoppingCart } from "@/type";

interface DialogState {
  open: boolean;
  close: () => void;
}

const useDialogStore = create<DialogState>((set) => ({
  open: false,
  close: () => set({ open: false }),
}));

const CheckoutContainer = styled("div", {
  label: "CheckoutContainer",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  backgroundColor: theme.componentStyleOverrides?.dialog?.backgroundColor,
  width: 462,
  maxWidth: "100vw",
  color: theme.palette.primary.main,
  ".bc_header_section": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "23px 28px 10px 28px",

    ".bc_header": {
      fontSize: 20,
      fontWeight: 700,
    },
    ".sub_header": {
      fontSize: 18,
      fontWeight: 600,
    },
  },

  ".product_section": {
    flex: 1,
    overflowY: "scroll",
    ...brandclubStyles.customScrollbar,
    padding: "23px 27px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      padding: "23px 5%",
    },
    ".cart_group": {
      marginBottom: 50,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        marginBottom: 30,
      },
    },
    ".group_info": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: 8,
      marginBottom: 30,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        marginTop: 10,
        marginBottom: 24,
      },
      ".group_count": {
        fontSize: 14,
        fontWeight: 500,
      },
      ".group_subtotal": {
        fontSize: 14,
        fontWeight: 500,
        ".bc-dtc-price": {
          fontWeight: 600,
        },
      },
    },
  },

  ".checkout_section": {
    display: "flex",
    flexDirection: "column",
    gap: "23px",
    padding: "23px 27px 37px 27px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      padding: "23px 5% 37px 5%",
    },
    boxShadow: "0 0 6px 0 rgba(119, 115, 115, 0.16)",
    ".subtotal": {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      fontSize: 18,
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
  },
}));

const CheckoutButton = styled("button")(({ theme }) => ({
  fontSize: 16,
  height: 48,
  fontWeight: 600,
  width: "100%",
  border: "none",
  borderRadius: theme.globalButtonRadius,
  display: "flex",
  justifyContent: "center",
  cursor: "pointer",
  alignItems: "center",

  backgroundColor:
    theme.componentStyleOverrides?.bagDrawerCheckoutButton?.backgroundColor ||
    theme.palette.primary.main,
  color:
    theme.componentStyleOverrides?.bagDrawerCheckoutButton?.textColor ||
    theme.palette.primary.contrastText,
  "&:hover": {
    backgroundColor:
      theme.componentStyleOverrides?.bagDrawerCheckoutButton?.backgroundColor ||
      theme.palette.primary.main,
    color:
      theme.componentStyleOverrides?.bagDrawerCheckoutButton?.textColor ||
      theme.palette.primary.contrastText,
  },
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: 14,
    height: 44,
  },
}));
const ViewBagButton = styled("button")(({ theme }) => ({
  fontSize: 16,
  height: 48,
  fontWeight: 600,
  width: "100%",
  borderRadius: theme.globalButtonRadius,
  cursor: "pointer",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  border: `1px solid ${
    theme.componentStyleOverrides?.bagDrawerViewBagButton?.borderColor ||
    "#dcdddc"
  }`,
  backgroundColor:
    theme.componentStyleOverrides?.bagDrawerViewBagButton?.backgroundColor ||
    "inherit",
  color:
    theme.componentStyleOverrides?.bagDrawerViewBagButton?.textColor ||
    theme.palette.primary.main,
  "&:hover": {
    backgroundColor:
      theme.componentStyleOverrides?.bagDrawerViewBagButton?.backgroundColor ||
      "inherit",
    color:
      theme.componentStyleOverrides?.bagDrawerViewBagButton?.textColor ||
      theme.palette.primary.main,
    border: `1px solid ${
      theme.componentStyleOverrides?.bagDrawerViewBagButton?.borderColor ||
      "#dcdddc"
    }`,
  },
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: 14,
    height: 44,
  },
}));

const StyledEmptyBagMessageCard = styled(EmptyBagMessageCard)({
  width: "auto",
  margin: "0 25px",
  marginTop: 50,
  ".store-message-card__content": {
    fontSize: 16,
    width: 282,
  },
  ".store-message-card__title": {
    fontSize: 20,
  },
  ".store-message-card__cta": {
    marginTop: 10,
  },
});

interface ProductSectionProps {
  loading: boolean;
  allCarts: UserRetailerShoppingCart[] | undefined;
}
const ProductSection: FC<ProductSectionProps> = ({ loading, allCarts }) => {
  if (loading) {
    return (
      <Box className="product_section">
        <CartProductLoading />
        <CartProductLoading />
      </Box>
    );
  }
  if (!allCarts) {
    return null;
  }
  return (
    <Box className="product_section">
      {allCarts.map((item) => (
        <Box
          key={`cartGroup:${item.shoppingCartId}`}
          display="flex"
          flexDirection="column"
          className="cart_group"
          gap={0}
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={"12px"}
          >
            <img
              src={getEntityImage(item.retailerId as number, "retailer")}
              alt={`Logo for retailer: ${item.retailerId}`}
              style={{ height: 24, width: 24 }}
            />
            <Typography
              sx={(theme) => ({
                fontWeight: 600,
                fontSize: 20,
                color: theme.palette.primary.main,
              })}
            >
              {getRetailerFromId(item.retailerId as number)}
            </Typography>
          </Box>
          <Box className="group_info">
            <Box className="group_count">
              {pluralize("item", sumItemQuantities(item?.items), true)}
            </Box>
            <Box className="group_subtotal">
              Subtotal:{" "}
              <span className="bc-dtc-price">
                {calculateTotal(item?.items ?? [])}
              </span>
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gap="30px">
            {(item?.items ?? []).map((product) => (
              <CartProduct
                product={product}
                key={`cart:${item?.shoppingCartId}:item:${product.stacklineSku}`}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

interface CheckoutSectionProps {
  initiating: boolean;
  allCarts: UserRetailerShoppingCart[] | undefined;
  closeDrawer: () => void;
}
const CheckoutSection: FC<CheckoutSectionProps> = ({
  initiating,
  closeDrawer,
  allCarts = [],
}) => {
  const bcRouting = useBCRouting();
  const [trackCheckoutBarAction] = useTrackActions();

  const onViewBag = async () => {
    trackCheckoutBarAction(AnalyticsTrackingEvent.VIEW_BAG, {});

    bcRouting.navigate(HOST_DOMAIN + "/mybag?action=checkout");
    closeDrawer();
  };

  const onCheckout = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const retailerIds = allCarts.map((cart) => cart.retailerId);

    trackCheckoutBarAction(AnalyticsTrackingEvent.BC_USER_REQUESTED_CHECKOUT, {
      cartItems: allCarts.map((cart) => ({
        items: cart.items,
        retailerId: cart.retailerId,
      })),
      retailerIds,
      retailerId: retailerIds[0],
      source: "brandclub_dtc_checkout_bar",
    });

    bcRouting.location.href = HOST_DOMAIN + "/checkout?action=checkout";
  };

  return (
    <Box className="checkout_section">
      <Box className="subtotal">
        <div>Subtotal</div>
        <div>{calculateTotalOfAllCarts(allCarts)}</div>
      </Box>
      <Box
        display="flex"
        gap="12px"
        width="100%"
        flexDirection="column"
        alignItems="center"
      >
        <CheckoutButton disabled={initiating} onClick={onCheckout}>
          {initiating ? "Creating cart..." : "Checkout"}
        </CheckoutButton>
        <ViewBagButton onClick={onViewBag}>View bag</ViewBagButton>
      </Box>
    </Box>
  );
};

const BagDrawerInner = () => {
  const { close } = useDialogStore();
  const { data: allCarts, isLoading: initializingCart } = useAllCarts({
    select: (data) =>
      data.userRetailerShoppingCarts.filter(
        (c) => !!c.items?.length && !!c.retailerId
      ),
    // prevent race conditions when adding an item to the cart triggered this component to mount
    // a premature refetch could cause the new updated cart to be overwritten with stale data
    refetchOnMount: false,
    enabled: false, // Prevents fetching
    staleTime: Infinity, // Prevents background refetches
  });

  return (
    <CheckoutContainer>
      <Box className="bc_header_section">
        <Box className="bc_header">Bag</Box>
        <CloseButton
          style={{
            all: "unset",
            cursor: "pointer",
          }}
          className="options"
          onClick={close}
          iconSize={15}
          disableRipple
        />
      </Box>
      {allCarts?.length === 0 ? (
        <StyledEmptyBagMessageCard />
      ) : (
        <>
          <ProductSection loading={initializingCart} allCarts={allCarts} />
          {
            <CheckoutSection
              initiating={false}
              allCarts={allCarts}
              closeDrawer={close}
            />
          }
        </>
      )}
    </CheckoutContainer>
  );
};
const BagDrawer = () => {
  const { open, close } = useDialogStore();

  return (
    <Drawer
      id="cart-popover"
      open={open}
      sx={{
        zIndex: 9999,
        width: 462,
      }}
      PaperProps={{
        sx: {
          boxShadow: "none",
          borderRadius: "0",
          ...brandclubStyles.customScrollbar,
        },
      }}
      slotProps={{
        backdrop: {
          sx: (theme) => ({
            display: "flex !important",
            backgroundColor: hexadecimal(theme.palette.primary.main)(21),
          }),
        },
      }}
      anchor="right"
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      onClose={() => {
        close();
      }}
    >
      <BagDrawerInner />
    </Drawer>
  );
};
BagDrawer.show = () => {
  useDialogStore.setState({
    open: true,
  });
};

export default BagDrawer;
